// Memoized attendee tile (SPEC §9.3.4, design "Meeting room" tile): keyed by
// identity, primitive props only, media attached via ref effects. A re-render
// never recreates or reattaches the <video> element (attach only runs when
// track epochs change). Speaking ring, mic-off badge, hand badge, name over a
// text shadow (legibility-over-video invariant), quality dot, and a CSS-only
// voice equalizer gated by the isSpeaking flag (no per-frame JS).

import { memo, useCallback, useEffect, useRef } from 'react';

import { engine } from '../engine';
import { probeRender } from '../perf/renderProbe';
import { setPinned, useCallStore } from '../store/callStore';
import { EqualizerBars } from './EqualizerBars';
import { Avatar } from '../ui/Avatar';
import { IconFullScreen, IconMicOff, IconPin } from '../ui/icons';

interface Props {
  identity: string;
  /** 'camera' renders the person; 'screen' renders their share (F-CALL-2). */
  source: 'camera' | 'screen';
}

export const ParticipantTile = memo(function ParticipantTile({ identity, source }: Props) {
  probeRender(`tile:${identity}:${source}`);

  const attendee = useCallStore((s) => s.attendees[identity]);
  const trackEpoch = useCallStore((s) => s.trackEpochs[identity] ?? 0);
  const pinned = useCallStore((s) => s.pinnedIdentity === identity);
  const videoRef = useRef<HTMLVideoElement>(null);

  const pin = useCallback(() => setPinned(identity), [identity]);
  const fullscreen = useCallback(() => {
    void videoRef.current?.parentElement?.requestFullscreen?.().catch(() => {});
  }, []);

  const hasVideo = source === 'camera' ? !!attendee?.cameraEnabled : !!attendee?.screenShareEnabled;

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !hasVideo) return;
    const track = engine.videoTrack(identity, source);
    if (!track) return;
    track.attach(el);
    return () => {
      track.detach(el);
    };
  }, [identity, source, hasVideo, trackEpoch]);

  if (!attendee) return null;

  return (
    <div
      className={`bz-tile${attendee.isSpeaking && source === 'camera' ? ' bz-tile-speaking' : ''}`}
      data-identity={identity}
    >
      {hasVideo ? (
        <video
          ref={videoRef}
          className={`bz-tile-video${source === 'screen' ? ' bz-tile-video-contain' : ''}${source === 'camera' && attendee.isLocal ? ' bz-tile-mirror' : ''}`}
          autoPlay
          playsInline
          muted
        />
      ) : (
        <span className="bz-tile-avatar">
          <Avatar
            name={attendee.name}
            identity={identity}
            isSelf={attendee.isLocal}
            size={76}
            photoUrl={attendee.avatarUrl}
          />
        </span>
      )}
      <div className="bz-tile-overlay">
        {source === 'camera' && !attendee.micEnabled && (
          <span className="bz-tile-muted" title="Microphone off">
            <IconMicOff size={11} />
          </span>
        )}
        <span className="bz-tile-name">
          {attendee.name}
          {attendee.isLocal ? ' (You)' : ''}
        </span>
        {attendee.encrypted === false && (
          <span className="bz-tile-plaintext" title="This attendee's media is not encrypted">
            ⚠︎
          </span>
        )}
      </div>
      {attendee.handRaisedAt !== null && source === 'camera' && (
        <span className="bz-tile-hand" title="Hand raised">
          ✋
        </span>
      )}
      {source === 'screen' && (
        <span className="bz-tile-presenting">
          {attendee.name}
          {attendee.isLocal ? ' (You)' : ''} · presenting
        </span>
      )}
      <span className={`bz-quality bz-quality-${attendee.quality}`} title="Connection quality" />
      {/* Remote tiles only: your own audio already shows on the mic pill, so the
          self-tile equalizer would be redundant. */}
      {source === 'camera' && attendee.micEnabled && !attendee.isLocal && (
        <EqualizerBars active={attendee.isSpeaking} className="bz-tile-eq" />
      )}
      <button
        type="button"
        className="bz-iconbtn bz-iconbtn-flat bz-tile-pinbtn"
        data-pinned={pinned}
        onClick={source === 'camera' ? pin : fullscreen}
        title={source === 'camera' ? (pinned ? 'Unpin' : 'Pin') : 'Fullscreen'}
      >
        {source === 'camera' ? <IconPin size={18} /> : <IconFullScreen size={18} />}
      </button>
    </div>
  );
});
